import { isEqual } from 'lodash-es'
import { ref } from '@vue/composition-api'
import { getStorage } from '@ms/utils/storage'

/**
 * Custom hook to manage state with optional caching and data fetching.
 *
 * @param {any} [initialState=null] - The initial state value.
 * @param {Object} [options={}] - Optional configuration object.
 * @param {string} [options.cacheKey=''] - Key to use for caching the state.
 * @param {Function} [options.fetchData=null] - Function to fetch data asynchronously.
 * @param {string} [options.cacheType='local'] - Type of storage for caching ('local' or 'session').
 * @returns {Object} - An object containing the state, loading status, error, and request function.
 * @returns {Ref} state - The reactive state object.
 * @returns {Ref} loading - The reactive loading status (true, false, -1).
 * @returns {Ref} error - The reactive error object.
 * @returns {Function} request - Function to fetch data and update the state.
 */
export function useState(initialState = null, { cacheKey = '', fetchData = null, cacheType = 'local' } = {}) {
  const defaultState = cacheKey ? getStorage(cacheKey, initialState, cacheType) : initialState
  const state = ref(defaultState)
  // true, false, -1
  const loading = ref(false)
  const error = ref(null)

  const setState = (newState) => {
    if (isEqual(state.value, newState)) return
    state.value = newState
    if (cacheKey) {
      const cacheValue = JSON.stringify(state.value)
      if (cacheType === 'local') {
        localStorage.setItem(cacheKey, cacheValue)
      } else if (cacheType === 'session') {
        sessionStorage.setItem(cacheKey, cacheValue)
      }
    }
  }

  const request = async () => {
    if (!fetchData) {
      console.warn('fetchData is required')
      return
    }

    loading.value = true
    error.value = null

    const { error: err, data } = await fetchData()

    if (err) {
      error.value = err
      loading.value = -1
    } else {
      setState(data)
      loading.value = false
    }
  }

  return {
    state,
    loading,
    error,
    request,
    setState
  }
}
